import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useStripe } from '../contexts/StripeContext';
import { Elements } from '@stripe/react-stripe-js';
import stripePromise, { STRIPE_CONFIG } from '../config/stripe';
import SubscriptionManager from '../components/SubscriptionManager';
import './LandingPage.css';
import {
  Search,
  Shield,
  Clock,
  CheckCircle,
  ArrowRight,
  Star,
  Menu,
  X,
  Play,
  BarChart3,
  Target,
  Zap,
  Mail,
  User,
  LogOut,
  CreditCard
} from 'lucide-react';

const LandingPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const { subscription } = useStripe();

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isUserMenuOpen && !event.target.closest('[data-user-menu]')) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  // Prevent background scrolling when modals are open
  useEffect(() => {
    if (isSubscriptionModalOpen || isVideoModalOpen || isContactModalOpen) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      const originalPosition = window.getComputedStyle(document.body).position;
      const originalTop = window.getComputedStyle(document.body).top;
      const scrollY = window.scrollY;

      document.body.classList.add('modal-open');
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';

      const preventTouchMove = (e) => {
        const modal = document.querySelector('[data-modal-content]');
        if (modal && !modal.contains(e.target)) {
          e.preventDefault();
        }
      };

      document.addEventListener('touchmove', preventTouchMove, {
        passive: false
      });
      document.addEventListener('touchstart', preventTouchMove, {
        passive: false
      });

      return () => {
        document.body.classList.remove('modal-open');
        document.body.style.overflow = originalStyle;
        document.body.style.position = originalPosition;
        document.body.style.top = originalTop;
        document.body.style.width = '';
        document.removeEventListener('touchmove', preventTouchMove);
        document.removeEventListener('touchstart', preventTouchMove);
        window.scrollTo(0, scrollY);
      };
    }
  }, [isSubscriptionModalOpen, isVideoModalOpen, isContactModalOpen]);

  const features = [
    {
      icon: Search,
      title: 'Complete Case Search',
      description:
        'Search through 190,000+ LA Superior Court cases with powerful filters by charges, judges, prosecutors, and more.'
    },
    {
      icon: Target,
      title: 'Similar Case Discovery',
      description:
        'Find cases similar to yours to build stronger arguments and negotiate better outcomes.'
    },
    {
      icon: BarChart3,
      title: 'Analytics & Insights',
      description:
        'Get case preparation insights, sentencing patterns, and attorney performance data.'
    },
    {
      icon: Clock,
      title: 'Real-time Updates',
      description:
        'Access the most current case information with automatic daily updates from court systems.'
    },
    {
      icon: Shield,
      title: 'Secure & Compliant',
      description:
        'Enterprise-grade security with full compliance for legal professionals and law firms.'
    },
    {
      icon: Zap,
      title: 'Save Time & Money',
      description:
        'Eliminate manual research and reduce court website fees. Find what you need in seconds.'
    }
  ];

  const testimonials = [
    {
      name: 'Christopher Chaney',
      role: 'Criminal Defense Attorney',
      firm: 'The Law Offices of Christopher Chaney',
      content:
        'Briefed has transformed how I prepare for cases. Finding similar cases used to take hours - now it takes minutes.',
      rating: 5
    }
  ];

  const pricingPlans = [
    {
      name: 'Pro Plan',
      price: `$${STRIPE_CONFIG.plans.monthly.amount / 100}`,
      period: `per ${STRIPE_CONFIG.plans.monthly.interval}`,
      description: 'Unlimited access for professional use',
      features: [
        '7-day free trial',
        'Unlimited searches',
        'Advanced analytics',
        'Full export capabilities',
        'Priority support',
        'Similar case discovery',
        'Real-time updates'
      ],
      cta: 'Start Free Trial',
      popular: true,
      isFree: false,
      annualPrice: `$${STRIPE_CONFIG.plans.annual.amount / 100}`,
      annualPeriod: `per ${STRIPE_CONFIG.plans.annual.interval}`,
      annualSavings: 'Save 2 months'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm border-b border-gray-100 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img
                src="/briefed_logo_transp.png"
                alt="Briefed Logo"
                className="h-8 w-auto"
              />
              <span className="ml-3 text-xl font-light text-gray-900">
                Briefed
              </span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <a
                href="#features"
                className="text-gray-600 hover:text-gray-900 transition-colors"
              >
                Features
              </a>
              <a
                href="#pricing"
                className="text-gray-600 hover:text-gray-900 transition-colors"
              >
                Pricing
              </a>
              <a
                href="#testimonials"
                className="text-gray-600 hover:text-gray-900 transition-colors"
              >
                Testimonials
              </a>

              {currentUser ? (
                <div className="relative" data-user-menu>
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 transition-colors"
                  >
                    <User className="h-5 w-5" />
                    <span>
                      {currentUser.displayName ||
                        currentUser.email?.split('@')[0] ||
                        'User'}
                    </span>
                  </button>

                  {isUserMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                      <div className="py-1">
                        <div className="px-4 py-2 text-sm text-gray-700 border-b border-gray-100">
                          <div className="font-medium">
                            {currentUser.displayName || 'User'}
                          </div>
                          <div className="text-gray-500 text-xs">
                            {currentUser.email}
                          </div>
                          {subscription && (
                            <div className="mt-1">
                              <span
                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                  subscription.status === 'active'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-blue-100 text-blue-800'
                                }`}
                              >
                                {subscription.status === 'active'
                                  ? 'Pro Plan'
                                  : 'Free Tier'}
                              </span>
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => {
                            navigate('/app');
                            setIsUserMenuOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                        >
                          <BarChart3 className="h-4 w-4" />
                          Go to App
                        </button>
                        {subscription && (
                          <button
                            onClick={() => {
                              navigate('/subscription');
                              setIsUserMenuOpen(false);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                          >
                            <CreditCard className="h-4 w-4" />
                            Manage Subscription
                          </button>
                        )}
                        <button
                          onClick={() => {
                            logout();
                            setIsUserMenuOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                        >
                          <LogOut className="h-4 w-4" />
                          Sign Out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => navigate('/login')}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Sign In
                </button>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-600 hover:text-gray-900"
              >
                {isMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100">
            <div className="px-4 py-4 space-y-4">
              <a
                href="#features"
                className="block text-gray-600 hover:text-gray-900"
              >
                Features
              </a>
              <a
                href="#pricing"
                className="block text-gray-600 hover:text-gray-900"
              >
                Pricing
              </a>
              <a
                href="#testimonials"
                className="block text-gray-600 hover:text-gray-900"
              >
                Testimonials
              </a>
              <button
                onClick={() => navigate(currentUser ? '/app' : '/login')}
                className="w-full bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                {currentUser ? 'Go to App' : 'Sign In'}
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-20 pb-16 bg-gradient-to-br from-blue-50 via-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {currentUser && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg max-w-md mx-auto">
                <p className="text-green-800 font-medium">
                  Welcome back,{' '}
                  {currentUser.displayName ||
                    currentUser.email?.split('@')[0] ||
                    'User'}
                  !
                </p>
                <p className="text-green-600 text-sm mt-1">
                  Ready to continue your case research?
                </p>
              </div>
            )}
            <h1 className="text-4xl md:text-6xl font-light text-gray-900 mb-6">
              Court Intelligence for
              <span className="block text-blue-600 font-medium">
                Legal Professionals
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Access complete case histories, find similar cases, and get
              insights from 190,000+ LA Superior Court cases. Save time, build
              stronger arguments, and negotiate better outcomes.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <button
                onClick={() => navigate(currentUser ? '/app' : '/register')}
                className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center"
              >
                {currentUser ? 'Go to App' : 'Start Free Trial'}
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
              {!currentUser && (
                <button
                  onClick={() => navigate('/login')}
                  className="border border-gray-300 text-gray-700 px-8 py-4 rounded-lg text-lg font-medium hover:bg-gray-50 transition-colors flex items-center justify-center"
                >
                  Sign In
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
              )}
              <button
                onClick={() => setIsVideoModalOpen(true)}
                className="border border-gray-300 text-gray-700 px-8 py-4 rounded-lg text-lg font-medium hover:bg-gray-50 transition-colors flex items-center justify-center"
              >
                <Play className="mr-2 h-5 w-5" />
                Watch Demo
              </button>
            </div>

            {/* Hero Image Placeholder */}
            <div className="relative max-w-4xl mx-auto">
              <div className="bg-gray-100 rounded-2xl shadow-2xl p-8 border border-gray-200">
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <div className="flex items-center mb-4">
                    <Search className="h-5 w-5 text-gray-400 mr-3" />
                    <div className="flex-1 bg-gray-50 rounded-lg px-4 py-3">
                      <span className="text-gray-500">
                        Search by case number, judge, charge, or attorney...
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="text-sm text-blue-600 font-medium mb-1">
                        Case #25BFCF1234
                      </div>
                      <div className="text-xs text-gray-600">
                        Burglary • Judge Martinez
                      </div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="text-sm text-green-600 font-medium mb-1">
                        Similar Cases Found
                      </div>
                      <div className="text-xs text-gray-600">
                        47 cases with similar charges
                      </div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <div className="text-sm text-purple-600 font-medium mb-1">
                        Sentencing Pattern
                      </div>
                      <div className="text-xs text-gray-600">
                        Avg: 18 months confinement
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-4">
              Everything you need for case preparation
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Briefed provides comprehensive court intelligence tools designed
              specifically for legal professionals.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                  <feature.icon className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-medium text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-light text-blue-600 mb-2">
                190,000+
              </div>
              <div className="text-gray-600">Court Cases</div>
            </div>
            <div>
              <div className="text-4xl font-light text-blue-600 mb-2">20+</div>
              <div className="text-gray-600">Courthouses</div>
            </div>
            <div>
              <div className="text-4xl font-light text-blue-600 mb-2">24/7</div>
              <div className="text-gray-600">Access</div>
            </div>
            <div>
              <div className="text-4xl font-light text-blue-600 mb-2">
                99.9%
              </div>
              <div className="text-gray-600">Uptime</div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-4">
              Trusted by legal professionals
            </h2>
            <p className="text-xl text-gray-600">
              See what attorneys are saying about Briefed
            </p>
          </div>

          <div className="flex justify-center">
            <div className="max-w-md">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="bg-gray-50 p-8 rounded-2xl">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star
                        key={i}
                        className="h-5 w-5 text-yellow-400 fill-current"
                      />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 italic">
                    &ldquo;{testimonial.content}&rdquo;
                  </p>
                  <div>
                    <div className="font-medium text-gray-900">
                      {testimonial.name}
                    </div>
                    <div className="text-sm text-gray-600">
                      {testimonial.role}
                    </div>
                    <div className="text-sm text-gray-500">
                      {testimonial.firm}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-4">
              Start your free trial today
            </h2>
            <p className="text-xl text-gray-600">
              Experience the full power of Briefed with no commitment
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 max-w-2xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className={`bg-white p-8 rounded-2xl border-2 ${plan.popular ? 'border-blue-500 relative' : 'border-gray-200'}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className="text-2xl font-medium text-gray-900 mb-2">
                    {plan.name}
                  </h3>
                  <div className="text-4xl font-light mb-1 text-gray-900">
                    {plan.price}
                  </div>
                  <div className="text-gray-600">{plan.period}</div>
                  {plan.annualPrice && (
                    <div className="mt-2">
                      <div className="text-2xl font-light text-gray-700">
                        {plan.annualPrice}
                      </div>
                      <div className="text-sm text-gray-500">
                        {plan.annualPeriod}
                      </div>
                      <div className="text-xs text-green-600 font-medium mt-1">
                        {plan.annualSavings}
                      </div>
                    </div>
                  )}
                  <p className="text-gray-600 mt-4">{plan.description}</p>
                </div>

                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="space-y-2">
                  <button
                    onClick={() => {
                      if (currentUser) {
                        setIsSubscriptionModalOpen(true);
                      } else {
                        navigate('/register');
                      }
                    }}
                    className="w-full py-4 rounded-lg font-medium transition-colors bg-blue-600 text-white hover:bg-blue-700"
                  >
                    {currentUser ? 'Start Free Trial' : 'Start Free Trial'}
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">
              Start your 7-day free trial • No credit card required
            </p>
            <p className="text-sm text-gray-500">
              Email verification required • Cancel anytime
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-light text-white mb-6">
            Ready to transform your case preparation?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Be among the first legal professionals to experience the power of
            comprehensive case intelligence.
          </p>
          <button
            onClick={() => navigate('/register')}
            className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-medium hover:bg-gray-50 transition-colors"
          >
            Start Free Trial
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center mb-4">
                <img
                  src="/briefed_logo_transp.png"
                  alt="Briefed Logo"
                  className="h-8 w-auto"
                />
                <span className="ml-3 text-xl font-light">Briefed</span>
              </div>
              <p className="text-gray-400 mb-4">
                Court intelligence platform for legal professionals. Access
                complete case histories and build stronger arguments.
              </p>
            </div>

            <div>
              <h3 className="font-medium mb-4">Product</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <a
                    href="#features"
                    className="hover:text-white transition-colors"
                  >
                    Features
                  </a>
                </li>
                <li>
                  <a
                    href="#pricing"
                    className="hover:text-white transition-colors"
                  >
                    Pricing
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-medium mb-4">Support</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <button
                    onClick={() => setIsContactModalOpen(true)}
                    className="hover:text-white transition-colors text-left"
                  >
                    Contact
                  </button>
                </li>
                <li>
                  <a
                    href="/privacy"
                    className="hover:text-white transition-colors"
                  >
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a
                    href="/terms"
                    className="hover:text-white transition-colors"
                  >
                    Terms of Service
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
            <p>
              &copy; {new Date().getFullYear()} Briefed. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Video Modal */}
      {isVideoModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 modal-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsVideoModalOpen(false);
            }
          }}
          onTouchStart={(e) => {
            if (e.target === e.currentTarget) {
              e.preventDefault();
              setIsVideoModalOpen(false);
            }
          }}
        >
          <div
            className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden modal-content"
            data-modal-content
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => {
              e.stopPropagation();
              const modalContent = e.currentTarget;
              const isScrollingModal =
                modalContent.scrollHeight > modalContent.clientHeight;
              if (!isScrollingModal) {
                e.preventDefault();
              }
            }}
            onTouchEnd={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-xl font-medium">Briefed Demo</h3>
              <button
                onClick={() => setIsVideoModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <Play className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Demo video placeholder</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Replace with actual demo video
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contact Modal */}
      {isContactModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 modal-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsContactModalOpen(false);
            }
          }}
          onTouchStart={(e) => {
            if (e.target === e.currentTarget) {
              e.preventDefault();
              setIsContactModalOpen(false);
            }
          }}
        >
          <div
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden modal-content"
            data-modal-content
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => {
              e.stopPropagation();
              const modalContent = e.currentTarget;
              const isScrollingModal =
                modalContent.scrollHeight > modalContent.clientHeight;
              if (!isScrollingModal) {
                e.preventDefault();
              }
            }}
            onTouchEnd={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-xl font-medium">Contact Us</h3>
              <button
                onClick={() => setIsContactModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">
                    Get in touch with our team
                  </h4>
                  <p className="text-gray-600 mb-6">
                    We&apos;re here to help with any questions about Briefed.
                    Choose your preferred contact method below.
                  </p>
                </div>

                <div className="space-y-4">
                  {/* Email Contact */}
                  <div className="flex items-center p-4 bg-gray-50 rounded-lg">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                      <Mail className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-medium text-gray-900">
                        Email Support
                      </h5>
                      <p className="text-sm text-gray-600 mb-2">
                        Get help via email - we typically respond within 24
                        hours
                      </p>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-mono bg-white px-2 py-1 rounded border">
                          support@sagaswipe.com
                        </span>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(
                              'support@sagaswipe.com'
                            );
                            // You could add a toast notification here
                          }}
                          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                        >
                          Copy
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Modal */}
      {isSubscriptionModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 modal-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsSubscriptionModalOpen(false);
            }
          }}
          onTouchStart={(e) => {
            if (e.target === e.currentTarget) {
              e.preventDefault();
              setIsSubscriptionModalOpen(false);
            }
          }}
        >
          <div
            className="w-full max-w-4xl max-h-[90vh] overflow-y-auto modal-content"
            data-modal-content
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => {
              e.stopPropagation();
              const modalContent = e.currentTarget;
              const isScrollingModal =
                modalContent.scrollHeight > modalContent.clientHeight;
              if (!isScrollingModal) {
                e.preventDefault();
              }
            }}
            onTouchEnd={(e) => e.stopPropagation()}
          >
            <Elements stripe={stripePromise}>
              <SubscriptionManager
                onClose={() => setIsSubscriptionModalOpen(false)}
              />
            </Elements>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
